/**
 * API Client for Vacation Tracker
 */

const BASE_URL = '/api';

function getAuthHeaders() {
  const sessionId = localStorage.getItem('sessionId');
  return {
    'Content-Type': 'application/json',
    'Authorization': sessionId ? `Bearer ${sessionId}` : ''
  };
}

async function request(method, path, body = null) {
  const options = {
    method,
    headers: getAuthHeaders()
  };

  if (body) {
    options.body = JSON.stringify(body);
  }

  const response = await fetch(`${BASE_URL}${path}`, options);
  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || 'Request failed');
  }

  return data;
}

// Dashboard
export const getDashboardStats = () => request('GET', '/dashboard');

// Users
export const getUsers = (status = 'active') => request('GET', `/users?status=${status}`);
export const getAllUsers = () => request('GET', '/users?status=all');
export const getUser = (id) => request('GET', `/users/${id}`);
export const updateUser = (id, data) => request('PUT', `/users/${id}`, data);
export const activateUser = (id) => request('PUT', `/users/${id}`, { is_active: true, is_deleted: false });
export const deactivateUser = (id) => request('PUT', `/users/${id}`, { is_active: false });
export const deleteUser = (id) => request('PUT', `/users/${id}`, { is_deleted: true });
export const restoreUser = (id) => request('PUT', `/users/${id}`, { is_deleted: false, is_active: true });
export const permanentlyDeleteUser = (id) => request('DELETE', `/users/${id}`);

// Leave Types
export const getLeaveTypes = () => request('GET', '/leave-types');
export const createLeaveType = (data) => request('POST', '/leave-types', data);
export const updateLeaveType = (id, data) => request('PUT', `/leave-types/${id}`, data);
export const deleteLeaveType = (id) => request('DELETE', `/leave-types/${id}`);

// Leave Requests
export const getLeaves = (params = {}) => {
  const query = new URLSearchParams(params).toString();
  return request('GET', `/leaves${query ? `?${query}` : ''}`);
};
export const createLeave = (data) => request('POST', '/leaves', data);
export const approveLeave = (id) => request('POST', `/leaves/${id}/approve`);
export const rejectLeave = (id, reason) => request('POST', `/leaves/${id}/reject`, { reason });
export const cancelLeave = (id) => request('DELETE', `/leaves/${id}`);

// Balances
export const getBalances = (params = {}) => {
  const query = new URLSearchParams(params).toString();
  return request('GET', `/balances${query ? `?${query}` : ''}`);
};
export const getAllUserBalances = (year) => request('GET', `/balances/all?year=${year}`);
export const updateUserBalance = (userId, leaveTypeId, year, data) =>
  request('PUT', `/balances/${userId}/${leaveTypeId}/${year}`, data);

// Departments
export const getDepartments = () => request('GET', '/departments');
export const createDepartment = (data) => request('POST', '/departments', data);
export const updateDepartment = (id, data) => request('PUT', `/departments/${id}`, data);
export const deleteDepartment = (id) => request('DELETE', `/departments/${id}`);
export const getDepartmentPolicies = (id) => request('GET', `/departments/${id}/policies`);
export const updateDepartmentPolicies = (id, policies) => request('PUT', `/departments/${id}/policies`, policies);

// Locations
export const getLocations = () => request('GET', '/locations');
export const createLocation = (data) => request('POST', '/locations', data);

// Holidays
export const getHolidays = (year) => request('GET', `/holidays?year=${year}`);
export const createHoliday = (data) => request('POST', '/holidays', data);
export const deleteHoliday = (id) => request('DELETE', `/holidays/${id}`);

// Calendar
export const getCalendarData = (start, end) =>
  request('GET', `/calendar?start=${start}&end=${end}`);

// Notifications
export const getNotificationSettings = () => request('GET', '/notifications');
export const updateNotificationSettings = (data) => request('PUT', '/notifications', data);
export const getSlackChannels = () => request('GET', '/slack/channels');

// TOIL (Time Off In Lieu)
export const getToil = (userId) => request('GET', `/toil?user_id=${userId}`);
export const addToil = (data) => request('POST', '/toil', data);

// Workspace Settings
export const getWorkspaceSettings = () => request('GET', '/workspace-settings');
export const updateWorkspaceSettings = (data) => request('PUT', '/workspace-settings', data);

// Slack User Import
export const searchSlackUsers = (query = '') => request('GET', `/slack/users/search?q=${encodeURIComponent(query)}`);
export const importSlackUser = (slackUserId) => request('POST', '/users/import', { slack_user_id: slackUserId });

// Admin
export const syncUsers = () => request('POST', '/admin/sync-users');
export const getSlackMemberCounts = () => request('GET', '/admin/slack-member-counts');
export const bulkImportFromSlack = (guestFilter = 'all') => request('POST', '/admin/bulk-import', { guestFilter });

// Data Management
export const getDataStats = () => request('GET', '/admin/data-stats');
export const downloadBackup = async () => {
  const sessionId = localStorage.getItem('sessionId');
  const response = await fetch('/api/admin/backup', {
    headers: {
      'Authorization': sessionId ? `Bearer ${sessionId}` : ''
    }
  });
  if (!response.ok) throw new Error('Failed to download backup');
  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `vacation-tracker-backup-${new Date().toISOString().split('T')[0]}.json`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
};
export const clearNonAdminUsers = () => request('DELETE', '/admin/clear-users');
export const clearLeaveHistory = () => request('DELETE', '/admin/clear-leave-history');
